import React, { useState, useEffect, useCallback, useMemo, useRef, memo } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  TextField,
  Card,
  CardContent,
  IconButton,
  Chip,
} from '@mui/material';
import BarChartIcon from '@mui/icons-material/BarChart';
import apiClient from '../utils/axios';
import { formatNumber, formatChangeRate } from '../utils/formatUtils';
import { isTradingHours, isNXTTradingHours, isKRXSessionOpen, isWeekend, isHolidaySync } from '../utils/tradingHours';
import { connectPricesWs } from '../utils/watchlistPricesWs';

// 종목별 카드 컴포넌트 (메모이제이션으로 최적화)
const StockCard = memo(({ 
  stockCode, 
  stockName, 
  priceInfo, 
  buyItems, 
  targetSellMap,
  priceLoading,
  onOpenTargetSellDialog,
}) => {
  const isLoading = priceLoading && !priceInfo;
  const price = priceInfo?.price || 0;
  const change = priceInfo?.change || 0;
  const changeRate = priceInfo?.changeRate || 0;
  const isPositive = change >= 0;
  const priceColor = price > 0 ? (isPositive ? 'error.main' : 'primary.main') : 'text.primary';

  // 매수 내역을 번호 순서대로 정렬 및 최근 매수 내역 계산 (한 번에 처리)
  const { sortedBuyItems, latestBuyItem } = useMemo(() => {
    const sorted = [...buyItems].sort((a, b) => new Date(a.dateTime) - new Date(b.dateTime));
    const latest = sorted.length > 0 ? sorted[sorted.length - 1] : null;
    return { sortedBuyItems: sorted, latestBuyItem: latest };
  }, [buyItems]);

  // 가장 최근 매수 내역의 auto_sell 상태 확인
  const latestTargetSell = useMemo(() => {
    if (!latestBuyItem) return null;
    const key = `${stockCode}_${latestBuyItem.dateTime}`;
    return targetSellMap.get(key) || null;
  }, [latestBuyItem, targetSellMap, stockCode]);

  const isAutoSellActive = latestTargetSell?.auto_sell === 'Y';

  const handleDialogOpen = useCallback(() => {
    onOpenTargetSellDialog({ stockCode, stockName });
  }, [stockCode, stockName, onOpenTargetSellDialog]);

  return (
    <Card sx={{ mb: 3, backgroundColor: '#E3F2FD' }}>
      <CardContent>
        {/* 헤더: 종목명, 종목코드, Last Stock price, 현재가, 등락률, 감시중, 아이콘 */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Box>
            <Box display="flex" alignItems="center" gap={1} mb={0.5} flexWrap="wrap">
              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                {stockName || stockCode} ({stockCode})
              </Typography>
              <Chip
                label={priceInfo?.stockMarket || 'KRX'}
                size="small"
                color="info"
                sx={{
                  fontWeight: 'bold',
                  backgroundColor: '#81D4FA',
                  color: '#01579B',
                }}
              />
              <Box display="flex" alignItems="center" gap={1}>
                {isAutoSellActive && (
                  <Typography variant="body1" sx={{ color: 'error.main', fontWeight: 'bold' }}>
                    감시중
                  </Typography>
                )}
                {isLoading ? (
                  <CircularProgress size={20} />
                ) : price > 0 && (
                  <>
                    <Typography variant="body1" sx={{ fontWeight: 'bold', color: priceColor }}>
                      {formatNumber(price)}
                    </Typography>
                    <Typography 
                      variant="body1" 
                      sx={{ 
                        color: isPositive ? 'error.main' : 'primary.main',
                        fontWeight: 'bold'
                      }}
                    >
                      ({formatChangeRate(changeRate)})
                    </Typography>
                  </>
                )}
              </Box>
            </Box>
          </Box>
          <IconButton size="small" onClick={handleDialogOpen}>
            <BarChartIcon />
          </IconButton>
        </Box>

        {/* 매수 내역 테이블 */}
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.100' }}>
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>No</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>Bought</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>Target</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>Qty</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                  Expect
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedBuyItems.map((item, index) => {
                const boughtPrice = parseFloat(item.buy_price || 0);
                const qty = parseInt(item.buy_qty || 0);
                // targetSellMap에서 buyDateTime을 key로 사용하여 매칭
                const key = `${stockCode}_${item.dateTime}`;
                const targetSell = targetSellMap.get(key);
                const targetPrice = targetSell?.target_price ? parseFloat(targetSell.target_price) : null;
                const expectProfit = targetPrice ? (targetPrice - boughtPrice) * qty : null;
                const expectRate = targetPrice && boughtPrice > 0 ? ((targetPrice - boughtPrice) / boughtPrice) * 100 : null;

                return (
                  <TableRow key={`${stockCode}_${item.dateTime}`}>
                    <TableCell align="center">{index + 1}</TableCell>
                    <TableCell align="center">{formatNumber(boughtPrice)}</TableCell>
                    <TableCell align="center">
                      {targetPrice ? formatNumber(targetPrice) : '-'}
                    </TableCell>
                    <TableCell align="center">{formatNumber(qty)}</TableCell>
                    <TableCell align="center">
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          {expectProfit !== null 
                            ? `${formatNumber(expectProfit)} (${expectRate.toFixed(2)}%)`
                            : '-'
                          }
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );
}, (prevProps, nextProps) => {
  // 커스텀 비교 함수: 필요한 props만 비교하여 불필요한 리렌더링 방지
  return (
    prevProps.stockCode === nextProps.stockCode &&
    prevProps.stockName === nextProps.stockName &&
    prevProps.priceInfo?.price === nextProps.priceInfo?.price &&
    prevProps.priceInfo?.change === nextProps.priceInfo?.change &&
    prevProps.priceInfo?.changeRate === nextProps.priceInfo?.changeRate &&
    prevProps.priceInfo?.stockMarket === nextProps.priceInfo?.stockMarket &&
    prevProps.buyItems.length === nextProps.buyItems.length &&
    prevProps.priceLoading === nextProps.priceLoading &&
    prevProps.targetSellMap === nextProps.targetSellMap
  );
});

StockCard.displayName = 'StockCard';

const Holdings = () => {
  const [holdings, setHoldings] = useState([]);
  const [prices, setPrices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [, setPriceLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [targetSellDialogOpen, setTargetSellDialogOpen] = useState(false);
  const [targetSellDialogStock] = useState(null);
  const [targetSellForms, setTargetSellForms] = useState({}); // { buyDateTime: { targetPrice, autoSell } }
  const [isInTradingHours, setIsInTradingHours] = useState(isTradingHours());
  const [isInNXTTradingHours, setIsInNXTTradingHours] = useState(isNXTTradingHours());
  const [isWeekendDay, setIsWeekendDay] = useState(isWeekend());
  const [isHolidayDay, setIsHolidayDay] = useState(isHolidaySync());
  const pricesWsRef = useRef(null);
  const [pricesWsReady, setPricesWsReady] = useState(false);
  const wasActiveMarketForPricesRef = useRef(false);

  // 보유종목 목록 조회
  const fetchHoldings = useCallback(async () => {
    try {
      setLoading(true);
      setErrorMessage(null);
      const response = await apiClient.get('/holdings');
      setHoldings(response.data || []);
    } catch (error) {
      console.error('[보유종목] 목록 조회 실패:', error);
      let message = '보유종목 목록을 불러오는 중 오류가 발생했습니다.';
      if (error.response?.status === 401) {
        message = '로그인이 필요합니다.';
      } else if (error.response?.data?.error) {
        message = error.response.data.error;
      }
      setErrorMessage(message);
      setHoldings([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // 현재가 REST 스냅샷 (장외만 — 장중은 WebSocket)
  const fetchPrices = useCallback(async (showLoading = true) => {
    if (holdings.length === 0) {
      setPrices([]);
      return;
    }

    const inMarket =
      !isWeekend() &&
      !isHolidaySync() &&
      (isKRXSessionOpen() || isNXTTradingHours());
    if (inMarket) {
      if (showLoading) setPriceLoading(false);
      return;
    }

    try {
      if (showLoading) setPriceLoading(true);
      const response = await apiClient.get('/holdings/prices');
      setPrices(response.data || []);
    } catch (error) {
      console.error('[보유종목] 현재가 조회 실패:', error);
    } finally {
      if (showLoading) setPriceLoading(false);
    }
  }, [holdings.length]);

  const upsertPriceFromWs = useCallback((data) => {
    if (!data?.stockCode) return;
    const code6 = String(data.stockCode).substring(0, 6);
    setPrices((prev) => {
      const next = [...prev];
      const idx = next.findIndex(
        (p) => String(p.stockCode || '').substring(0, 6) === code6
      );
      const merged = {
        ...(idx >= 0 ? next[idx] : {}),
        stockCode: idx >= 0 ? next[idx].stockCode : data.stockCode,
        stockName: data.stockName || (idx >= 0 ? next[idx].stockName : '') || '',
        stockMarket: data.stockMarket || (idx >= 0 ? next[idx].stockMarket : 'KRX') || 'KRX',
        price: data.price,
        change: data.change,
        changeRate: data.changeRate,
      };
      if (idx >= 0) next[idx] = merged;
      else next.push(merged);
      return next;
    });
  }, []);

  useEffect(() => {
    const check = () => {
      setIsInTradingHours(isTradingHours());
      setIsInNXTTradingHours(isNXTTradingHours());
      setIsWeekendDay(isWeekend());
      setIsHolidayDay(isHolidaySync());
    };
    check();
    const t = setInterval(check, 60000);
    return () => clearInterval(t);
  }, []);

  // 가격 조회 (목록 로드 시 REST)
  useEffect(() => {
    if (holdings.length > 0) {
      fetchPrices(true);
    } else {
      setPrices([]);
    }
  }, [holdings, fetchPrices]);

  const fetchPricesRef = useRef(fetchPrices);
  fetchPricesRef.current = fetchPrices;

  // 장중 WebSocket
  useEffect(() => {
    const isActiveMarket = !isWeekendDay && !isHolidayDay && (isInTradingHours || isInNXTTradingHours || isKRXSessionOpen());
    const token = typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null;

    if (!isActiveMarket || !token) {
      if (pricesWsRef.current) {
        pricesWsRef.current.close();
        pricesWsRef.current = null;
      }
      setPricesWsReady(false);
      if (wasActiveMarketForPricesRef.current) {
        fetchPricesRef.current(false);
      }
      wasActiveMarketForPricesRef.current = false;
      return undefined;
    }

    wasActiveMarketForPricesRef.current = true;

    const conn = connectPricesWs({
      token,
      onOpen: () => setPricesWsReady(true),
      onClose: () => setPricesWsReady(false),
      onMessage: (msg) => {
        if (msg.type === 'price' && msg.data) {
          upsertPriceFromWs(msg.data);
          setPriceLoading(false);
        } else if (msg.type === 'status' && msg.status === 'connected') {
          setPricesWsReady(true);
        }
      },
    });
    pricesWsRef.current = conn;

    return () => {
      conn.close();
      if (pricesWsRef.current === conn) {
        pricesWsRef.current = null;
      }
      setPricesWsReady(false);
    };
  }, [
    upsertPriceFromWs,
    isWeekendDay,
    isHolidayDay,
    isInTradingHours,
    isInNXTTradingHours,
  ]);

  useEffect(() => {
    if (!pricesWsReady || !pricesWsRef.current) return;
    const seen = new Set();
    const items = [];
    for (const h of holdings) {
      const code = String(h.stockCode || '').substring(0, 6);
      if (!code || seen.has(code)) continue;
      seen.add(code);
      items.push({
        stockCode: h.stockCode,
        stockName: h.stockName || code,
        stockMarket: h.stockMarket || 'KRX',
      });
    }
    pricesWsRef.current.send({ type: 'set_holdings', items });
  }, [holdings, pricesWsReady, isInTradingHours, isInNXTTradingHours]);

  // 가격 정보 맵 생성
  const priceMap = useMemo(() => {
    const map = new Map();
    prices.forEach(p => {
      map.set(p.stockCode, p);
    });
    return map;
  }, [prices]);


  // 종목별로 그룹화된 보유종목 데이터
  const groupedHoldings = useMemo(() => {
    const grouped = new Map();
    
    holdings.forEach(item => {
      const stockCode = item.stockCode;
      if (!grouped.has(stockCode)) {
        grouped.set(stockCode, {
          stockCode,
          buyItems: [],
        });
      }
      grouped.get(stockCode).buyItems.push(item);
    });

    return Array.from(grouped.values());
  }, [holdings]);

  // 가장 최근 매수 내역 찾기 헬퍼 함수
  const getLatestBuyItem = useCallback((stockCode) => {
    const stockHoldings = holdings.filter(h => h.stockCode === stockCode);
    if (stockHoldings.length === 0) return null;
    return [...stockHoldings].sort((a, b) => new Date(b.dateTime) - new Date(a.dateTime))[0];
  }, [holdings]);

  // 다이얼로그 닫기 핸들러
  const handleCloseDialog = useCallback(() => {
    setTargetSellDialogOpen(false);
  }, []);

  // 자동매도감시 설정 저장 (가장 마지막에 매수한 종목 하나만)
  const handleSaveTargetSell = useCallback(async () => {
    if (!targetSellDialogStock) return;

    try {
      setErrorMessage(null);
      
      const latestBuyItem = getLatestBuyItem(targetSellDialogStock.stockCode);
      
      if (!latestBuyItem) {
        setErrorMessage('매수 내역을 찾을 수 없습니다.');
        return;
      }
      
      const form = targetSellForms[latestBuyItem.dateTime];
      if (!form) {
        setErrorMessage('설정 정보를 찾을 수 없습니다.');
        return;
      }
        
      if (!form.targetPrice || parseFloat(form.targetPrice) <= 0) {
        setErrorMessage('매도목표가는 0보다 큰 값이어야 합니다.');
        return;
      }
        
      // 목표가격 1의 자리를 0으로 처리 (버림)
      const targetPriceValue = parseFloat(form.targetPrice);
      const roundedTargetPrice = Math.floor(targetPriceValue / 10) * 10;
        
      // 매도수량은 매수수량 전량으로 설정
      const targetQty = parseInt(latestBuyItem.buy_qty);
      
      // 확인 창 표시
      const confirmMessage = `자동매도 설정을 저장하시겠습니까?\n\n종목: ${targetSellDialogStock.stockName || targetSellDialogStock.stockCode}\n매도목표가: ${roundedTargetPrice.toLocaleString()}원\n매도수량: ${targetQty.toLocaleString()}주\n자동매도: ${form.autoSell ? '활성화' : '비활성화'}`;
      
      if (!window.confirm(confirmMessage)) {
        return; // 사용자가 취소한 경우
      }
        
      await apiClient.post('/holdings/target-sell', {
        stockCode: targetSellDialogStock.stockCode,
        buyDateTime: latestBuyItem.dateTime,
        targetPrice: roundedTargetPrice,
        targetQty: targetQty,
        autoSell: form.autoSell ? 'Y' : 'N',
      });
      
      setTargetSellDialogOpen(false);
    } catch (error) {
      const message = error.response?.data?.error || '자동매도감시 설정 중 오류가 발생했습니다.';
      setErrorMessage(message);
    }
  }, [targetSellDialogStock, targetSellForms, getLatestBuyItem]);

  // 초기 로드
  useEffect(() => {
    fetchHoldings();
  }, [fetchHoldings]);


  return (
    <Container maxWidth="lg">
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
      </Box>

      {errorMessage && (
        <Alert severity="warning" sx={{ mb: 3 }} onClose={() => setErrorMessage(null)}>
          {errorMessage}
        </Alert>
      )}

      {/* 보유종목 테이블 */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.100' }}>
              <TableCell align="center" sx={{ fontWeight: 'bold' }}>종목명</TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold' }}>종목코드</TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold' }}>거래소</TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold' }}>현재가</TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold' }}>등락률</TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold' }}>No</TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold' }}>Bought</TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold' }}>Target</TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold' }}>Qty</TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold' }}>Expect</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={10} align="center">
                  <Box display="flex" justifyContent="center" alignItems="center" py={3}>
                    <CircularProgress />
                    <Typography variant="body2" sx={{ ml: 2 }}>
                      보유종목을 불러오는 중...
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : groupedHoldings.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} align="center">
                  <Typography variant="body2" color="text.secondary" py={3}>
                    보유종목이 없습니다.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              groupedHoldings.flatMap((group) => {
                const priceInfo = priceMap.get(group.stockCode);
                const stockName = priceInfo?.stockName || group.stockCode;
                const price = priceInfo?.price || 0;
                const change = priceInfo?.change || 0;
                const changeRate = priceInfo?.changeRate || 0;
                const isPositive = change >= 0;
                const priceColor = price > 0 ? (isPositive ? 'error.main' : 'primary.main') : 'text.primary';
                
                // 매수 내역을 차수 순서대로 정렬
                const sortedBuyItems = [...group.buyItems].sort((a, b) => (a.stage || 0) - (b.stage || 0));
                
                return sortedBuyItems.map((item, itemIndex) => {
                  const boughtPrice = parseFloat(item.buy_price || 0);
                  const qty = parseInt(item.buy_qty || 0);
                  const targetPrice = parseFloat(item.sell_price || 0);
                  const expectProfit = targetPrice > 0 ? (targetPrice - boughtPrice) * qty : null;
                  const expectRate = targetPrice > 0 && boughtPrice > 0 ? ((targetPrice - boughtPrice) / boughtPrice) * 100 : null;
                  
                  return (
                    <TableRow key={`${group.stockCode}_${item.stage}_${item.dateTime}`}>
                      {itemIndex === 0 && (
                        <>
                          <TableCell rowSpan={sortedBuyItems.length} align="center" sx={{ fontWeight: 'bold' }}>
                            {stockName}
                          </TableCell>
                          <TableCell rowSpan={sortedBuyItems.length} align="center">
                            {group.stockCode}
                          </TableCell>
                          <TableCell rowSpan={sortedBuyItems.length} align="center">
                            <Chip
                              label={priceInfo?.stockMarket || 'KRX'}
                              size="small"
                              color="info"
                              sx={{
                                fontWeight: 'bold',
                                backgroundColor: '#81D4FA',
                                color: '#01579B',
                              }}
                            />
                          </TableCell>
                          <TableCell rowSpan={sortedBuyItems.length} align="center">
                            <Typography variant="body2" sx={{ fontWeight: 'bold', color: priceColor }}>
                              {price > 0 ? formatNumber(price) : '-'}
                            </Typography>
                          </TableCell>
                          <TableCell rowSpan={sortedBuyItems.length} align="center">
                            {price > 0 ? (
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  color: isPositive ? 'error.main' : 'primary.main',
                                  fontWeight: 'bold'
                                }}
                              >
                                {formatChangeRate(changeRate)}
                              </Typography>
                            ) : '-'}
                          </TableCell>
                        </>
                      )}
                      <TableCell align="center">{item.stage || '-'}</TableCell>
                      <TableCell align="center">{formatNumber(boughtPrice)}</TableCell>
                      <TableCell align="center">
                        {targetPrice > 0 ? formatNumber(targetPrice) : '-'}
                      </TableCell>
                      <TableCell align="center">{formatNumber(qty)}</TableCell>
                      <TableCell align="center">
                        {expectProfit !== null 
                          ? `${formatNumber(expectProfit)} (${expectRate.toFixed(2)}%)`
                          : '-'
                        }
                      </TableCell>
                    </TableRow>
                  );
                });
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* 자동매도감시 설정 다이얼로그 */}
      <Dialog
        open={targetSellDialogOpen}
        onClose={handleCloseDialog}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          자동매도감시 설정
          {targetSellDialogStock && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {targetSellDialogStock.stockName || targetSellDialogStock.stockCode} ({targetSellDialogStock.stockCode})
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            {targetSellDialogStock && (() => {
              const latestBuyItem = getLatestBuyItem(targetSellDialogStock.stockCode);
              
              if (!latestBuyItem) {
                return (
                  <Typography variant="body2" color="text.secondary">
                    매수 내역이 없습니다.
                  </Typography>
                );
              }
              
              const form = targetSellForms[latestBuyItem.dateTime] || {};
              
              return (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'grey.100' }}>
                        <TableCell align="center" sx={{ fontWeight: 'bold' }}>매수일시</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 'bold' }}>매수가</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 'bold' }}>매수수량</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 'bold' }}>매도목표가</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 'bold' }}>자동매도</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                            <TableCell align="center">
                              <Typography variant="body2">
                            {latestBuyItem.dateTime}
                              </Typography>
                            </TableCell>
                        <TableCell align="center">{formatNumber(latestBuyItem.buy_price)}</TableCell>
                        <TableCell align="center">{formatNumber(latestBuyItem.buy_qty)}</TableCell>
                            <TableCell align="center">
                              <TextField
                                type="number"
                                size="small"
                                value={form.targetPrice || ''}
                                onChange={(e) => {
                                  setTargetSellForms({
                                    [latestBuyItem.dateTime]: {
                                      ...form,
                                      targetPrice: e.target.value,
                                    },
                                  });
                                }}
                                placeholder="목표가"
                                sx={{ width: '100px' }}
                                InputProps={{
                                  endAdornment: <Typography variant="caption">원</Typography>
                                }}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <Switch
                                size="small"
                                checked={form.autoSell || false}
                                onChange={(e) => {
                                  setTargetSellForms({
                                    [latestBuyItem.dateTime]: {
                                      ...form,
                                      autoSell: e.target.checked,
                                    },
                                  });
                                }}
                              />
                            </TableCell>
                          </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              );
            })()}
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              가장 최근 매수 내역의 매도목표가를 설정할 수 있습니다. 매도수량은 매수수량 전량으로 자동 설정됩니다. 자동매도 활성화 시 매도목표가에 도달하면 자동으로 매도 주문이 실행됩니다.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>취소</Button>
          <Button onClick={handleSaveTargetSell} variant="contained" color="primary">
            저장
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Holdings;
